// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import HomeAccordion from "@steiner/App/Components/Home/HomeAccordion";
import loadAccountSummaryDashboard from "@steiner/App/Handlers/AccountSummary/LoadAccountSummaryDashboard";
import { AccountSummaryModel } from "@steiner/App/Services/AccountSummary";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import DisplayPieChart from "@steiner/App/Widgets/AccountSummary/DisplayPieChart";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import { getSystemColor } from "@steiner/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    return {
        financialAccess: state.context.session?.properties?.financialAccess === "true",
    };
};

interface OwnProps {}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const LabelTypography = ({
    name,
    value,
    styles,
}: {
    name: string;
    value?: string;
    styles?: TypographyPresentationProps;
}) => {
    if (!value) {
        return null;
    }

    return (
        <Typography {...styles}>
            <Typography weight="bold">{`${name}: `}</Typography>
            {value}
        </Typography>
    );
};

export interface AccountSummaryDashboardStyles {
    container?: InjectableCss;
    infoContainer?: InjectableCss;
    chartContainer?: InjectableCss;
    labelText?: TypographyPresentationProps;
}

export const accountSummaryDashboardStyles: AccountSummaryDashboardStyles = {};

const styles = accountSummaryDashboardStyles;
const AccountSummaryDashboard: React.FC<Props> = ({ financialAccess }) => {
    const { currency, invoiceHistoryPageLink } = useAppSelector(state => ({
        currency: getSession(state).currency?.currencySymbol,
        invoiceHistoryPageLink: getPageLinkByPageType(state, "InvoiceHistoryPage"),
    }));
    const [accountSummaryInfo, setAccountSummaryInfo] = useState<AccountSummaryModel | null>(null);
    const [pieChartValues, setPieChartValues] = useState<any[]>([]);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(
            loadAccountSummaryDashboard({
                onSuccess: result => {
                    setAccountSummaryInfo(result);
                },
            }) as any,
        );
    }, []);

    useEffect(() => {
        pieChartCalculation();
    }, [accountSummaryInfo?.currentBalance, accountSummaryInfo?.availableCredit]);

    const pieChartCalculation = () => {
        let curBalance = parseFloat(accountSummaryInfo?.currentBalance.replace(/[$,]/g, "") || "");
        let availBalance = parseFloat(accountSummaryInfo?.availableCredit.replace(/[$,]/g, "") || "");

        if (curBalance <= 0 && availBalance >= 0) {
            curBalance = 0;
            availBalance = 100;
        } else if (availBalance <= 0) {
            curBalance = 100;
            availBalance = 0;
        }

        setPieChartValues([
            {
                name: "Current Balance *",
                value: curBalance,
                displayValue: accountSummaryInfo?.currentBalance,
                color: getSystemColor("common.pieChart2"),
            },
            {
                name: "Available Balance",
                value: availBalance,
                color: getSystemColor("common.pieChart"),
                displayValue: translate(
                    `{0} of {1}`,
                    `${accountSummaryInfo?.availableCredit || 0}`,
                    accountSummaryInfo?.creditLimit || "",
                ),
            },
        ]);
    };

    const showWidget = () => {
        return !!(
            accountSummaryInfo &&
            (accountSummaryInfo.userHasFinancialPermission ||
                accountSummaryInfo.haveOpenOrders ||
                (accountSummaryInfo.haveOpenInvoices && (accountSummaryInfo.isAdmin || accountSummaryInfo.isBuyer3)) ||
                (accountSummaryInfo.havePendingApprovalItems &&
                    (accountSummaryInfo.isAdmin || accountSummaryInfo.isBuyer3)))
        );
    };

    if (!showWidget() || !financialAccess) {
        return null;
    }

    return (
        <HomeAccordion
            title={translate("Account Snapshot")}
            isLoading={false}
            showPagination={false}
            viewAll={invoiceHistoryPageLink?.url}
        >
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.infoContainer}>
                    <LabelTypography
                        name={translate("Last Purchase")}
                        value={accountSummaryInfo?.lastPurchase}
                        styles={styles.labelText}
                    />
                    <LabelTypography
                        name={translate("Last Payment")}
                        value={accountSummaryInfo?.lastPayment}
                        styles={styles.labelText}
                    />
                    <LabelTypography
                        name={translate("MTD Purchases")}
                        value={accountSummaryInfo?.mtdPurchases}
                        styles={styles.labelText}
                    />
                    <LabelTypography
                        name={translate("YTD Purchases")}
                        value={accountSummaryInfo?.ytdPurchases}
                        styles={styles.labelText}
                    />
                    <LabelTypography
                        name={translate("Payment Days")}
                        value={accountSummaryInfo?.paymentDays}
                        styles={styles.labelText}
                    />
                    <LabelTypography
                        name={translate("Open Orders")}
                        value={accountSummaryInfo?.numberOfOpenOrders}
                        styles={styles.labelText}
                    />
                    <LabelTypography
                        name={translate("Open Invoices")}
                        value={accountSummaryInfo?.numberOfOpenInvoices}
                        styles={styles.labelText}
                    />
                    <LabelTypography
                        name={translate("Pending Approval")}
                        value={accountSummaryInfo?.numberOfPendingApprovalItems}
                        styles={styles.labelText}
                    />
                    <LabelTypography
                        name={translate("* Terms")}
                        value={accountSummaryInfo?.terms}
                        styles={styles.labelText}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.chartContainer}>
                    <DisplayPieChart data={pieChartValues} />
                </StyledWrapper>
            </StyledWrapper>
        </HomeAccordion>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(AccountSummaryDashboard),
    definition: {
        group: XNGAGE_ELEMENTS,
        icon: "headset",
    },
};

export default widgetModule;
